/**
 * Geo-based consent logic.
 *
 * EU/EEA + UK + Switzerland require explicit opt-in for analytics (GDPR/UK GDPR).
 * Everywhere else (notably US/CCPA) we use a soft notice with implied consent —
 * we set analytics to granted by default and offer an opt-out.
 *
 * Country detection comes from Cloudflare's `cf-ipcountry` header via /api/geo.
 * If detection fails we err on the side of strict consent (treat as EU).
 */

// EU member states (27)
const EU_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);

// EEA non-EU + UK + Switzerland (also enforce GDPR-equivalent rules)
const GDPR_EXTRA = new Set(['IS', 'LI', 'NO', 'GB', 'CH']);

export function isStrictConsentRegion(country: string | null | undefined): boolean {
  if (!country) return false; // Unknown → assume US-style implied consent
  const c = country.toUpperCase();
  return EU_COUNTRIES.has(c) || GDPR_EXTRA.has(c);
}

interface GeoInfo {
  country: string | null;
  isStrict: boolean;
}

let cachedGeo: GeoInfo | null = null;
let inflightFetch: Promise<GeoInfo> | null = null;

export async function getGeoInfo(): Promise<GeoInfo> {
  if (cachedGeo) return cachedGeo;
  if (inflightFetch) return inflightFetch;

  inflightFetch = (async () => {
    try {
      const resp = await fetch('/api/geo', { credentials: 'omit' });
      if (!resp.ok) throw new Error(`geo fetch ${resp.status}`);
      const data = await resp.json() as { country?: string };
      const country = (data.country || 'XX').toUpperCase();
      const info: GeoInfo = {
        country: country === 'XX' ? null : country,
        isStrict: isStrictConsentRegion(country),
      };
      cachedGeo = info;
      return info;
    } catch {
      // On failure default to US-style implied consent
      const info: GeoInfo = { country: null, isStrict: false };
      cachedGeo = info;
      return info;
    } finally {
      inflightFetch = null;
    }
  })();

  return inflightFetch;
}
